<template>
  <section class="mx-auto max-w-2xl" aria-labelledby="selfscan-panel-title">
    <button
      v-if="state === 'activation' || state === 'countdown' || state === 'ticketInfo'"
      type="button"
      class="mb-5 inline-flex items-center gap-2 font-semibold text-headings focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50 enabled:cursor-pointer enabled:hover:opacity-70"
      :disabled="submitting"
      @click="$emit('back')"
    >
      <DropdownIcon class="h-3 w-3 rotate-90 fill-current" />
      {{ $t('common.back') }}
    </button>

    <template v-if="state === 'activation'">
      <h1 id="selfscan-panel-title" class="h2">{{ $t('selfscan.confirm_title') }}</h1>

      <div class="mt-5 flex items-center gap-3 rounded-md border border-primary bg-primary/10 p-4" role="note">
        <DangerTriangleIcon class="mt-0.5 h-6 w-6 shrink-0 text-primary" />
        <p class="font-semibold text-headings">{{ $t('selfscan.warning_description') }}</p>
      </div>
    </template>

    <div v-if="state === 'countdown'" class="overflow-hidden rounded-md border border-surface-grey bg-surface-light p-6 sm:p-8" aria-live="polite">
      <div class="mx-auto max-w-sm text-center">
        <div class="relative mx-auto flex h-48 w-48 max-w-full items-center justify-center">
          <span class="selfscan-check-ring absolute h-24 w-24 rounded-full bg-primary/35" aria-hidden="true" />
          <span class="selfscan-check-ring absolute h-32 w-32 rounded-full bg-primary/35 [animation-delay:800ms]" aria-hidden="true" />
          <span class="selfscan-check-ring absolute h-40 w-40 rounded-full bg-primary/25 [animation-delay:1600ms]" aria-hidden="true" />
          <span class="selfscan-check-ring absolute h-48 w-48 rounded-full bg-primary/25 [animation-delay:2400ms]" aria-hidden="true" />
          <span class="selfscan-check-ring absolute h-56 w-56 rounded-full bg-primary/15 [animation-delay:3200ms]" aria-hidden="true" />
          <span class="selfscan-check-ring absolute h-64 w-64 rounded-full bg-primary/15 [animation-delay:4000ms]" aria-hidden="true" />
          <div class="selfscan-check relative z-10 flex h-28 w-28 items-center justify-center rounded-full bg-primary text-primary-text shadow-[0_0_32px_color-mix(in_srgb,var(--color-primary)_55%,transparent)]">
            <CheckmarkIcon class="h-8 w-12 fill-current" />
          </div>
        </div>
        <p class="mt-2 text-lg font-semibold text-primary" aria-hidden="true">
          {{ countdownSeconds }}
        </p>
        <h1 id="selfscan-panel-title" class="mt-2 h3 !text-primary">{{ $t('selfscan.activated') }}</h1>
        <span class="sr-only">{{ $t('selfscan.countdown_seconds', { count: countdownSeconds }) }}</span>
      </div>

      <div class="mt-8 rounded-md bg-surface-grey-light p-[18px] text-left">
        <h2 class="h4 border-b border-surface-grey pb-4">{{ event.name }}</h2>
        <dl class="mt-3">
          <div class="grid gap-1 py-2">
            <dt class="text-xs font-normal uppercase text-text">{{ $t('selfscan.ticket_type') }}</dt>
            <dd class="text-headings">{{ ticket.name1 }}</dd>
          </div>
          <div class="grid gap-1 py-2">
            <dt class="text-xs font-normal uppercase text-text">{{ $t('selfscan.ticket_number') }}</dt>
            <dd class="break-all text-headings">{{ ticket.ticket_number }}</dd>
          </div>
          <div class="grid gap-1 py-2">
            <dt class="text-xs font-normal uppercase text-text">{{ $t('selfscan.activation_time') }}</dt>
            <dd class="text-headings">{{ formatTimestamp(ticket.arrived || 0) }}</dd>
          </div>
        </dl>
      </div>
    </div>

    <div v-else class="mt-5 overflow-hidden rounded-md border border-surface-grey bg-surface-light p-6 sm:p-8">
      <div v-if="state === 'ticketInfo'" class="mb-6 rounded-md bg-surface-light p-6 text-center">
        <h1 id="selfscan-panel-title" class="h3 !text-primary">{{ $t('selfscan.success_title') }}</h1>
        <p class="mt-2 text-text">{{ $t('selfscan.ticket_info_description') }}</p>
      </div>

      <div class="rounded-md bg-surface-grey-light p-[18px]">
        <h2 class="h4 border-b border-surface-grey pb-4">{{ event.name }}</h2>

        <dl class="mt-3">
          <div class="grid gap-1 py-2">
            <dt class="text-xs font-normal uppercase text-text">{{ $t('selfscan.ticket_type') }}</dt>
            <dd class="text-headings">{{ ticket.name1 }}</dd>
          </div>
          <div v-if="state === 'activation'" class="grid gap-1 py-2">
            <dt class="text-xs font-normal uppercase text-text">{{ $t('selfscan.event_time') }}</dt>
            <dd class="text-headings">{{ formatTimestamp(event.eventtime) }}</dd>
          </div>
          <div class="grid gap-1 py-2">
            <dt class="text-xs font-normal uppercase text-text">{{ $t('selfscan.ticket_number') }}</dt>
            <dd class="break-all text-headings">{{ ticket.ticket_number }}</dd>
          </div>
          <div v-if="state === 'ticketInfo'" class="grid gap-1 py-2">
            <dt class="text-xs font-normal uppercase text-text">{{ $t('selfscan.activation_time') }}</dt>
            <dd class="text-headings">{{ formatTimestamp(ticket.arrived || 0) }}</dd>
          </div>
        </dl>
      </div>

      <template v-if="state === 'activation'">
        <div class="mt-6">
          <p v-if="error" class="mb-5 text-sm text-error" role="alert">{{ $t('selfscan.activation_error') }}</p>

          <SelfscanActivationSlider
            :busy="submitting"
            :reset-key="sliderResetKey"
            @complete="$emit('activate')"
          />
        </div>
      </template>

    </div>
  </section>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';

  import CheckmarkIcon from '@/components/icons/CheckmarkIcon.vue';
  import DangerTriangleIcon from '@/components/icons/DangerTriangleIcon.vue';
  import DropdownIcon from '@/components/icons/DropdownIcon.vue';
  import SelfscanActivationSlider from '@/components/selfscan/SelfscanActivationSlider.vue';
  import type { SelfscanTicket } from '@/types';
  import { formatDate } from '@/utils/formatDate';

  import type { SelfscanDisplayEvent } from './SelfscanTicketList.vue';

  defineProps<{
    state: 'activation' | 'countdown' | 'ticketInfo';
    event: SelfscanDisplayEvent;
    ticket: SelfscanTicket;
    submitting: boolean;
    error: boolean;
    countdownSeconds: number;
    sliderResetKey: number;
  }>();

  defineEmits<{
    back: [];
    activate: [];
  }>();

  const { locale } = useI18n();

  function formatTimestamp(timestamp: number) {
    return formatDate(timestamp, { locale: locale.value, showTime: true });
  }
</script>

<style scoped>
  .selfscan-check {
    animation: selfscan-check-in 100ms ease-out both;
  }

  .selfscan-check-ring {
    animation: selfscan-check-ring 2.6s ease-out infinite;
  }

  @keyframes selfscan-check-in {
    0% { opacity: 0; transform: scale(0.4); }
    65% { transform: scale(1.1); }
    100% { opacity: 1; transform: scale(1); }
  }

  @keyframes selfscan-check-ring {
    0% {
      opacity: 0.45;
      transform: scale(1);
    }
    10% {
      opacity: 0.85;
    }
    95% {
      opacity: 0.08;
      transform: scale(1.8);
    }
    100% {
      opacity: 0;
      transform: scale(1.8);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .selfscan-check,
    .selfscan-check-ring {
      animation: none;
    }

    .selfscan-check-ring { opacity: 0.3; }
  }
</style>